/**
 * Career Scout Signals wrappers.
 *
 * Exposes `useCareerScoutLogger()` for product events (called from components)
 * and module-level `logCareerScoutInfo/Warn/Error` for operational events (safe
 * to call from any context, including outside the React tree). Both paths read
 * the shared envelope from CareerScoutLoggingContext and abstract AvroOptional
 * wrapping from callers.
 */

import type { GenericEventElement, GenericEventScreen, GenericEventTag } from "./constants";
import type { GenericEventType } from "../native-modules";
import type { OperationalErrorSource } from "@indeed/signals-schema/com/indeed/signals/schema/careerscout/OperationalErrorSource";

import {
  SESSIONID_UNAVAILABLE,
  getCareerScoutLoggingEnvelope,
  useCareerScoutLoggingContext,
  type CareerScoutLoggingEnvelope,
} from "./CareerScoutLoggingContext";
import {
  operationalErrorSourceToAvro,
  operationalSourceToAvro,
  resolveOperationalErrorSource,
  resolveOperationalSource,
  type OperationalSourceTag,
} from "./operationalMetrics";
import {
  chatBackendToAvro,
  productEventNameToAvro,
  resolveChatBackend,
  resolveProductEventName,
  type ChatBackendTag,
  type ProductEventNameTag,
} from "./productMetrics";
import { getTabSessionTk } from "./tabSessionTk";

import {
  Level,
  log as logOperationalEvent,
  type OperationalEventV2,
} from "@indeed/signals-schema/com/indeed/signals/schema/careerscout/OperationalEventV2";
import {
  type AgentType,
  AvroOptional,
  type DwellFrom,
  EventType,
  log as logProductEvent,
  type MastraEventName,
  type ProductEventV2,
  type Screen,
} from "@indeed/signals-schema/com/indeed/signals/schema/careerscout/ProductEventV2";
import { useCallback, useMemo } from "react";

/** Optional context fields a caller can attach to any product event. */
export interface ProductContextParams {
  conversationId?: string;
  messageId?: string;
  agentType?: keyof typeof AgentType;
  numUserMessages?: number;
  messageContent?: string;
  jobKey?: string;
  isACEApply?: boolean;
  rjpTk?: string;
  jobOccupationSuid?: string;
  suggestion?: string;
  suggestions?: string;
  groupId?: string;
  suggestionId?: string;
  numSuggestions?: number;
  section?: string;
  before?: string;
  changes?: string;
  progress?: number;
  timeToFirstTokenMs?: number;
  totalResponseTimeMs?: number;
  timeToFirstToolCallMs?: number;
  /** Enum tag for the careerScout.mastraLatency Datadog metric. */
  mastraEventName?: keyof typeof MastraEventName;
  from?: string;
  /** Enum tag for the careerScout.aceDwell Datadog metric. */
  dwellFrom?: keyof typeof DwellFrom;
  /** Optional override for the careerScout.product.eventCount Datadog metric tag. */
  productEventName?: ProductEventNameTag;
  /** Optional override for the careerScout.mastra* Datadog metric chatBackend tag. */
  chatBackend?: ChatBackendTag;
  url?: string;
  duration?: number;
  label?: string;
  value?: string;
  actionKey?: string;
  available?: boolean;
  triggeredByPushNotification?: boolean;
  messagingConversationId?: string;
  pageTitle?: string;
  pageIndex?: number;
  errorMessage?: string;
  missingData?: string;
  recommendationMessage?: string;
  recommendationAgentType?: string;
}

const SCREEN_MAP: Record<GenericEventScreen, keyof typeof Screen> = {
  coWorker: "CO_WORKER",
  aceHub: "ACE_HUB",
  coWorkerPromo: "CO_WORKER_PROMO",
};

const screenToAvro = (screenName: GenericEventScreen | undefined): ProductEventV2["screen"] => {
  if (!screenName) {
    return null;
  }
  return { "com.indeed.signals.schema.careerscout.Screen": SCREEN_MAP[screenName] };
};

const agentTypeToAvro = (
  agentType: keyof typeof AgentType | undefined,
): ProductEventV2["agentType"] => {
  if (!agentType) {
    return null;
  }
  return { "com.indeed.signals.schema.careerscout.AgentType": agentType };
};

const mastraEventNameToAvro = (
  mastraEventName: keyof typeof MastraEventName | undefined,
): ProductEventV2["mastraEventName"] => {
  if (!mastraEventName) {
    return null;
  }
  return { "com.indeed.signals.schema.careerscout.MastraEventName": mastraEventName };
};

const dwellFromToAvro = (
  dwellFrom: keyof typeof DwellFrom | undefined,
): ProductEventV2["dwellFrom"] => {
  if (!dwellFrom) {
    return null;
  }
  return { "com.indeed.signals.schema.careerscout.DwellFrom": dwellFrom };
};

const eventTypeToSchema = (eventType: GenericEventType): keyof typeof EventType => {
  switch (eventType) {
    case "impression":
      return EventType.IMPRESSION;
    case "interaction":
      return EventType.INTERACTION;
    case "navigation":
      return EventType.NAVIGATION;
    default:
      return EventType.OTHER;
  }
};

// timestamp and tabSessionTk are NOT included here — they must be evaluated
// fresh at each log call (see buildPayload in useCareerScoutLogger), not
// captured inside the envelope memo, or events logged after the memo is
// created would all share a stale time and a potentially-rotated session token.
const buildCommonProductPayload = (
  envelope: CareerScoutLoggingEnvelope,
): Omit<
  ProductEventV2,
  "eventType" | "eventName" | "elementName" | "tags" | "timestamp" | "tabSessionTk"
> => ({
  commonDetails: AvroOptional.record("com.indeed.signals.common.CommonEventDetails", {
    ctk: AvroOptional.string(envelope.ctk),
    ipAddress: null,
  }),
  platform: { "com.indeed.signals.schema.reactnative.RNPlatformsV5": envelope.platform },
  osVersion: AvroOptional.string(envelope.osVersion),
  nativeAppVersion: AvroOptional.string(envelope.nativeAppVersion),
  rnLibraryVersion: AvroOptional.string(envelope.rnLibraryVersion),
  rnAppBundleVersion: AvroOptional.string(envelope.rnAppBundleVersion),
  variant: envelope.variant ?? null,
  env: envelope.env ?? null,
  manufacturer: envelope.manufacturer ?? null,
  model: envelope.model ?? null,
  brand: envelope.brand ?? null,
  systemName: envelope.systemName ?? null,
  deviceId: AvroOptional.string(envelope.deviceId),
  sessionId: SESSIONID_UNAVAILABLE,
  grp: envelope.proctorGroupsString ? [envelope.proctorGroupsString] : [],
  accountKey: AvroOptional.string(envelope.accountKey),
  country: AvroOptional.string(envelope.country),
  lang: AvroOptional.string(envelope.lang),
  ctk: AvroOptional.string(envelope.ctk),
  useragentraw: AvroOptional.string(envelope.useragentraw),
  assistantReplyMessageId: null,
  screen: null,
  conversationId: null,
  messageId: null,
  agentType: null,
  numUserMessages: null,
  messageContent: null,
  jobKey: null,
  isACEApply: null,
  rjpTk: null,
  jobOccupationSuid: null,
  suggestion: null,
  suggestions: null,
  groupId: null,
  suggestionId: null,
  numSuggestions: null,
  section: null,
  before: null,
  changes: null,
  progress: null,
  timeToFirstTokenMs: null,
  totalResponseTimeMs: null,
  timeToFirstToolCallMs: null,
  mastraEventName: null,
  productEventName: null,
  chatBackend: null,
  from: null,
  dwellFrom: null,
  url: null,
  duration: null,
  label: null,
  value: null,
  actionKey: null,
  available: null,
  triggeredByPushNotification: null,
  messagingConversationId: null,
  pageTitle: null,
  pageIndex: null,
  errorMessage: null,
  missingData: null,
  recommendationMessage: null,
  recommendationAgentType: null,
});

const applyProductContext = (params: ProductContextParams): Partial<ProductEventV2> => ({
  conversationId: AvroOptional.string(params.conversationId),
  messageId: AvroOptional.string(params.messageId),
  agentType: agentTypeToAvro(params.agentType),
  numUserMessages: AvroOptional.int(params.numUserMessages),
  messageContent: AvroOptional.string(params.messageContent),
  jobKey: AvroOptional.string(params.jobKey),
  isACEApply: AvroOptional.boolean(params.isACEApply),
  rjpTk: AvroOptional.string(params.rjpTk),
  jobOccupationSuid: AvroOptional.string(params.jobOccupationSuid),
  suggestion: AvroOptional.string(params.suggestion),
  suggestions: AvroOptional.string(params.suggestions),
  groupId: AvroOptional.string(params.groupId),
  suggestionId: AvroOptional.string(params.suggestionId),
  numSuggestions: AvroOptional.int(params.numSuggestions),
  section: AvroOptional.string(params.section),
  before: AvroOptional.string(params.before),
  changes: AvroOptional.string(params.changes),
  progress: AvroOptional.int(params.progress),
  timeToFirstTokenMs: AvroOptional.int(params.timeToFirstTokenMs),
  totalResponseTimeMs: AvroOptional.int(params.totalResponseTimeMs),
  timeToFirstToolCallMs: AvroOptional.int(params.timeToFirstToolCallMs),
  mastraEventName: mastraEventNameToAvro(params.mastraEventName),
  from: AvroOptional.string(params.from),
  dwellFrom: dwellFromToAvro(params.dwellFrom),
  url: AvroOptional.string(params.url),
  duration: AvroOptional.int(params.duration),
  label: AvroOptional.string(params.label),
  value: AvroOptional.string(params.value),
  actionKey: AvroOptional.string(params.actionKey),
  available: AvroOptional.boolean(params.available),
  triggeredByPushNotification: AvroOptional.boolean(params.triggeredByPushNotification),
  messagingConversationId: AvroOptional.string(params.messagingConversationId),
  pageTitle: AvroOptional.string(params.pageTitle),
  pageIndex: AvroOptional.int(params.pageIndex),
  errorMessage: AvroOptional.string(params.errorMessage),
  missingData: AvroOptional.string(params.missingData),
  recommendationMessage: AvroOptional.string(params.recommendationMessage),
  recommendationAgentType: AvroOptional.string(params.recommendationAgentType),
});

interface BaseProductParams extends ProductContextParams {
  elementName: GenericEventElement;
  assistantMessageId: string | null;
  screenName?: GenericEventScreen;
  additionalTags?: GenericEventTag[];
}

const applyProductMetricTags = (
  envelope: CareerScoutLoggingEnvelope | null,
  {
    eventName,
    elementName,
    additionalTags,
    mastraEventName,
    productEventName,
    chatBackend,
  }: Pick<
    BaseProductParams,
    "screenName" | "additionalTags" | "mastraEventName" | "productEventName" | "chatBackend"
  > & { eventName: string; elementName?: GenericEventElement },
): Pick<ProductEventV2, "productEventName" | "chatBackend"> => ({
  productEventName: productEventNameToAvro(
    resolveProductEventName({ eventName, elementName, productEventName }),
  ),
  chatBackend: chatBackendToAvro(
    resolveChatBackend({
      proctorGroupsString: envelope?.proctorGroupsString,
      additionalTags,
      mastraEventName,
      chatBackend,
    }),
  ),
});

interface GenericProductParams extends Omit<BaseProductParams, "elementName"> {
  elementName?: GenericEventElement;
  eventName: string;
  eventType: GenericEventType;
}

interface GenericSpecificEventParams extends ProductContextParams {
  eventName: string;
  elementName: GenericEventElement;
  assistantMessageId: string | null;
  screenName?: GenericEventScreen;
  additionalTags?: GenericEventTag[];
}

/**
 * Hook exposing the Career Scout product-event log wrappers. Must be used
 * within CareerScoutLoggingContextProvider — the hook reads the envelope from
 * context, so without a provider it throws.
 */
export const useCareerScoutLogger = () => {
  const envelope = useCareerScoutLoggingContext();
  const commonPayload = useMemo(() => buildCommonProductPayload(envelope), [envelope]);

  const buildPayload = useCallback(
    (
      eventType: keyof typeof EventType,
      eventName: string,
      {
        elementName,
        assistantMessageId,
        screenName,
        additionalTags = [],
        ...contextParams
      }: Omit<BaseProductParams, "elementName"> & { elementName?: GenericEventElement },
    ): ProductEventV2 => {
      const metricTags = applyProductMetricTags(envelope, {
        eventName,
        elementName,
        screenName,
        additionalTags,
        mastraEventName: contextParams.mastraEventName,
        productEventName: contextParams.productEventName,
        chatBackend: contextParams.chatBackend,
      });

      return {
        ...commonPayload,
        ...applyProductContext(contextParams),
        ...metricTags,
        timestamp: Date.now(),
        tabSessionTk: AvroOptional.string(getTabSessionTk()),
        eventType,
        eventName,
        elementName: elementName ?? eventName,
        tags: ["coWorker", ...additionalTags],
        screen: screenToAvro(screenName ?? "coWorker"),
        assistantReplyMessageId: AvroOptional.string(assistantMessageId),
      };
    },
    [commonPayload, envelope],
  );

  const logCareerScoutImpression = useCallback(
    (params: BaseProductParams) => {
      logProductEvent(buildPayload(EventType.IMPRESSION, "uiElement", params));
    },
    [buildPayload],
  );

  const logCareerScoutButtonTap = useCallback(
    (params: BaseProductParams) => {
      logProductEvent(buildPayload(EventType.INTERACTION, "tapButton", params));
    },
    [buildPayload],
  );

  const logCareerScoutGenericEvent = useCallback(
    ({ eventName, eventType, ...rest }: GenericProductParams) => {
      logProductEvent(buildPayload(eventTypeToSchema(eventType), eventName, rest));
    },
    [buildPayload],
  );

  const logCareerScoutGenericImpression = useCallback(
    ({ eventName, ...rest }: GenericSpecificEventParams) => {
      logProductEvent(buildPayload(EventType.IMPRESSION, eventName, rest));
    },
    [buildPayload],
  );

  const logCareerScoutGenericInteraction = useCallback(
    ({ eventName, ...rest }: GenericSpecificEventParams) => {
      logProductEvent(buildPayload(EventType.INTERACTION, eventName, rest));
    },
    [buildPayload],
  );

  return {
    logCareerScoutImpression,
    logCareerScoutButtonTap,
    logCareerScoutGenericEvent,
    logCareerScoutGenericImpression,
    logCareerScoutGenericInteraction,
  };
};

const logProductAtModuleLevel = (
  eventType: keyof typeof EventType,
  eventName: string,
  {
    elementName,
    assistantMessageId,
    screenName,
    additionalTags = [],
    ...contextParams
  }: Omit<BaseProductParams, "elementName"> & { elementName?: GenericEventElement },
): void => {
  const envelope = getCareerScoutLoggingEnvelope();
  if (!envelope) {
    return;
  }
  const metricTags = applyProductMetricTags(envelope, {
    eventName,
    elementName,
    screenName,
    additionalTags,
    mastraEventName: contextParams.mastraEventName,
    productEventName: contextParams.productEventName,
    chatBackend: contextParams.chatBackend,
  });

  logProductEvent({
    ...buildCommonProductPayload(envelope),
    ...applyProductContext(contextParams),
    ...metricTags,
    timestamp: Date.now(),
    tabSessionTk: AvroOptional.string(getTabSessionTk()),
    eventType,
    eventName,
    elementName: elementName ?? eventName,
    tags: ["coWorker", ...additionalTags],
    screen: screenToAvro(screenName ?? "coWorker"),
    assistantReplyMessageId: AvroOptional.string(assistantMessageId),
  });
};

/**
 * Module-level Signals impression logger. Safe to call outside of React
 * components — reads the envelope via getCareerScoutLoggingEnvelope().
 */
export const logCareerScoutProductImpression = (params: BaseProductParams): void =>
  logProductAtModuleLevel(EventType.IMPRESSION, "uiElement", params);

/** Module-level Signals tapButton logger. */
export const logCareerScoutProductButtonTap = (params: BaseProductParams): void =>
  logProductAtModuleLevel(EventType.INTERACTION, "tapButton", params);

/** Module-level Signals impression logger that uses a custom eventName. */
export const logCareerScoutProductGenericImpression = ({
  eventName,
  ...rest
}: GenericSpecificEventParams): void =>
  logProductAtModuleLevel(EventType.IMPRESSION, eventName, rest);

/**
 * Module-level Signals generic event logger that accepts both eventName and
 * eventType at runtime. Used for navigation, generic-other, and similar events
 * that don't fit the impression/buttonTap categories. elementName falls back
 * to eventName when no real UI element applies (e.g. navigation events).
 */
export const logCareerScoutProductGenericEvent = ({
  eventName,
  eventType,
  elementName,
  ...rest
}: Omit<GenericProductParams, "elementName"> & {
  elementName?: GenericEventElement;
}): void =>
  logProductAtModuleLevel(eventTypeToSchema(eventType), eventName, {
    ...rest,
    elementName: elementName ?? (eventName as GenericEventElement),
  });

/** Fields accepted by the operational log wrappers (info/warn/error). */
export interface OperationalParams {
  source: string;
  message: string;
  threadId?: string;
  messageId?: string;
  reason?: string;
  messageCount?: number;
  status?: number;
  errorMessage?: string;
  errorSource?: string;
  /** Optional override for the careerScout.operational.* Datadog metric tag. */
  operationalSource?: OperationalSourceTag;
  /** Optional override for the careerScout.operational.errorCount Datadog metric tag. */
  operationalErrorSource?: OperationalErrorSource;
  timeToFirstTokenMs?: number;
  totalResponseTimeMs?: number;
  timeToFirstToolCallMs?: number;
}

const buildCommonOperationalPayload = (
  envelope: CareerScoutLoggingEnvelope | null,
): Omit<OperationalEventV2, "level" | "source" | "message"> => {
  const base: Omit<OperationalEventV2, "level" | "source" | "message"> = {
    commonDetails: null,
    platform: null,
    osVersion: null,
    nativeAppVersion: null,
    rnLibraryVersion: null,
    rnAppBundleVersion: null,
    variant: null,
    env: null,
    manufacturer: null,
    model: null,
    brand: null,
    systemName: null,
    timestamp: Date.now(),
    sessionId: SESSIONID_UNAVAILABLE,
    grp: [],
    accountKey: null,
    country: null,
    lang: null,
    ctk: null,
    useragentraw: null,
    threadId: null,
    messageId: null,
    reason: null,
    messageCount: null,
    status: null,
    errorMessage: null,
    errorSource: null,
    operationalSource: null,
    operationalErrorSource: null,
    timeToFirstTokenMs: null,
    totalResponseTimeMs: null,
    timeToFirstToolCallMs: null,
  };
  if (!envelope) {
    // Bootstrap-error path: emit with minimal envelope rather than drop the event.
    return base;
  }
  return {
    ...base,
    commonDetails: AvroOptional.record("com.indeed.signals.common.CommonEventDetails", {
      ctk: AvroOptional.string(envelope.ctk),
      ipAddress: null,
    }),
    platform: { "com.indeed.signals.schema.reactnative.RNPlatformsV5": envelope.platform },
    osVersion: AvroOptional.string(envelope.osVersion),
    nativeAppVersion: AvroOptional.string(envelope.nativeAppVersion),
    rnLibraryVersion: AvroOptional.string(envelope.rnLibraryVersion),
    rnAppBundleVersion: AvroOptional.string(envelope.rnAppBundleVersion),
    variant: envelope.variant ?? null,
    env: envelope.env ?? null,
    manufacturer: envelope.manufacturer ?? null,
    model: envelope.model ?? null,
    brand: envelope.brand ?? null,
    systemName: envelope.systemName ?? null,
    grp: envelope.proctorGroupsString ? [envelope.proctorGroupsString] : [],
    accountKey: AvroOptional.string(envelope.accountKey),
    country: AvroOptional.string(envelope.country),
    lang: AvroOptional.string(envelope.lang),
    ctk: AvroOptional.string(envelope.ctk),
    useragentraw: AvroOptional.string(envelope.useragentraw),
  };
};

const logAtLevel = (level: keyof typeof Level, params: OperationalParams): void => {
  const envelope = getCareerScoutLoggingEnvelope();
  const operationalSource = resolveOperationalSource({
    source: params.source,
    proctorGroupsString: envelope?.proctorGroupsString,
    operationalSource: params.operationalSource,
  });
  const operationalErrorSource = resolveOperationalErrorSource({
    level,
    errorSource: params.errorSource,
    reason: params.reason,
    status: params.status,
    operationalErrorSource: params.operationalErrorSource,
  });

  logOperationalEvent({
    ...buildCommonOperationalPayload(envelope),
    level,
    source: params.source,
    message: params.message,
    operationalSource: operationalSourceToAvro(operationalSource),
    threadId: AvroOptional.string(params.threadId),
    messageId: AvroOptional.string(params.messageId),
    reason: AvroOptional.string(params.reason),
    messageCount: AvroOptional.int(params.messageCount),
    status: AvroOptional.int(params.status),
    errorMessage: AvroOptional.string(params.errorMessage),
    errorSource: AvroOptional.string(params.errorSource),
    operationalErrorSource: operationalErrorSourceToAvro(operationalErrorSource),
    timeToFirstTokenMs: AvroOptional.int(params.timeToFirstTokenMs),
    totalResponseTimeMs: AvroOptional.int(params.totalResponseTimeMs),
    timeToFirstToolCallMs: AvroOptional.int(params.timeToFirstToolCallMs),
  });
};

/** Emits an OperationalEvent at level=INFO. Safe to call from any context. */
export const logCareerScoutInfo = (params: OperationalParams): void =>
  logAtLevel(Level.INFO, params);

/** Emits an OperationalEvent at level=WARN. Safe to call from any context. */
export const logCareerScoutWarn = (params: OperationalParams): void =>
  logAtLevel(Level.WARN, params);

/** Emits an OperationalEvent at level=ERROR. Safe to call from any context. */
export const logCareerScoutError = (params: OperationalParams): void =>
  logAtLevel(Level.ERROR, params);
